import { auth, db } from '@/lib/firebase'
import { notificationService } from '@/services/notificationService'
import type { Company, CompanyInput } from '@/types'
import { type CompanyUser, type CompanyUserInput, CompanyUserRole } from '@/types/companyUser'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { newCompanyUserID } from './utils'

export const COMPANIES_COLLECTION = 'companies'
export const COMPANIES_USERS_COLLECTION = 'companiesUsers'

// Query Functions - Refactored for React Query compatibility

export const getCompanyById = async (companyID: string): Promise<Company> => {
  if (!companyID || companyID.trim() === '') {
    throw new Error('Company ID is required')
  }

  try {
    const docRef = doc(db, COMPANIES_COLLECTION, companyID)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      throw new Error(`Company with ID "${companyID}" not found`)
    }

    return { id: docSnap.id, ...docSnap.data() } as Company
  } catch (error) {
    // Re-throw the error if it's already our custom error
    if (error instanceof Error && error.message.includes('not found')) {
      throw error
    }

    // Wrap Firebase errors in a more descriptive error
    throw new Error(`Failed to fetch company: ${(error as Error).message}`)
  }
}

export const getAllCompanies = async (maxItems = 20): Promise<Company[]> => {
  if (maxItems <= 0) {
    throw new Error('maxItems must be greater than 0')
  }

  try {
    const q = query(collection(db, COMPANIES_COLLECTION), limit(maxItems))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Company)
  } catch (error) {
    throw new Error(`Failed to fetch companies: ${(error as Error).message}`)
  }
}

export const getCompaniesByIDs = async (companiesIDs: string[]): Promise<Company[]> => {
  if (!companiesIDs || companiesIDs.length === 0) {
    throw new Error('Company IDs array cannot be empty')
  }

  // Filter out empty/invalid IDs
  const validIDs = companiesIDs.filter((id) => id && id.trim() !== '')
  if (validIDs.length === 0) {
    throw new Error('No valid company IDs provided')
  }

  try {
    const q = query(collection(db, COMPANIES_COLLECTION), where('id', 'in', validIDs))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Company)
  } catch (error) {
    throw new Error(`Failed to fetch companies by IDs: ${(error as Error).message}`)
  }
}

export const getCompanyUsers = async (companyID: string): Promise<CompanyUser[]> => {
  if (!companyID || companyID.trim() === '') {
    throw new Error('Company ID is required')
  }

  try {
    const q = query(collection(db, COMPANIES_USERS_COLLECTION), where('companyID', '==', companyID))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as CompanyUser)
  } catch (error) {
    throw new Error(`Failed to fetch company users: ${(error as Error).message}`)
  }
}

export const getUserJoinedCompanies = async (userID: string): Promise<CompanyUser[]> => {
  if (!userID || userID.trim() === '') {
    throw new Error('User ID is required')
  }

  try {
    const q = query(collection(db, COMPANIES_USERS_COLLECTION), where('userID', '==', userID))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as CompanyUser)
  } catch (error) {
    throw new Error(`Failed to fetch user's joined companies: ${(error as Error).message}`)
  }
}

export const getUserCompanies = async (
  userID: string,
): Promise<{
  companies: Company[]
  roles: Map<string, CompanyUser>
}> => {
  if (!userID || userID.trim() === '') {
    throw new Error('User ID is required')
  }

  try {
    const roles = new Map<string, CompanyUser>()

    // Get companies where user is a member
    const userJoinedCompanies = await getUserJoinedCompanies(userID)
    const joinedCompaniesIDs = userJoinedCompanies
      .map((companyUser) => {
        if (!companyUser.companyID) return ''
        roles.set(companyUser.companyID, companyUser)
        return companyUser.companyID
      })
      .filter(Boolean)

    // Get companies owned by the user
    const ownedCompanies = await getOwnedCompanies(userID)

    // Add owned companies to roles map with owner role
    for (const company of ownedCompanies) {
      if (company.id && !roles.has(company.id)) {
        // Create a virtual CompanyUser entry for owned companies
        roles.set(company.id, {
          id: `owner_${company.id}`,
          userID,
          companyID: company.id,
          role: CompanyUserRole.ADMIN,
          isOwner: true,
          createdAt: company.createdAt,
          updatedAt: company.updatedAt,
        } as CompanyUser)
      }
    }

    // Combine all company IDs
    const allCompanyIDs = [...new Set([...joinedCompaniesIDs, ...ownedCompanies.map((c) => c.id).filter(Boolean)])]

    if (allCompanyIDs.length === 0) {
      return { companies: [], roles: new Map<string, CompanyUser>() }
    }

    // Get all companies (both joined and owned)
    const joinedCompaniesData = joinedCompaniesIDs.length > 0 ? await getCompaniesByIDs(joinedCompaniesIDs) : []
    const allCompanies = [...joinedCompaniesData, ...ownedCompanies]

    // Remove duplicates based on company ID
    const uniqueCompanies = allCompanies.filter(
      (company, index, self) => index === self.findIndex((c) => c.id === company.id),
    )

    return { companies: uniqueCompanies, roles }
  } catch (error) {
    throw new Error(`Failed to fetch user companies: ${(error as Error).message}`)
  }
}

export const getOwnedCompanies = async (userID: string): Promise<Company[]> => {
  if (!userID || userID.trim() === '') {
    throw new Error('User ID is required')
  }

  try {
    // Query for companies where user is currentOwnerID
    const currentOwnerQuery = query(collection(db, COMPANIES_COLLECTION), where('currentOwnerID', '==', userID))
    const currentOwnerSnapshot = await getDocs(currentOwnerQuery)

    // Query for companies where user is createdByUserID (fallback for older data)
    const createdByQuery = query(collection(db, COMPANIES_COLLECTION), where('createdByUserID', '==', userID))
    const createdBySnapshot = await getDocs(createdByQuery)

    // Combine results and remove duplicates
    const allCompanies = new Map<string, Company>()

    // Add companies from currentOwnerID query
    for (const doc of currentOwnerSnapshot.docs) {
      allCompanies.set(doc.id, { id: doc.id, ...doc.data() } as Company)
    }

    // Add companies from createdByUserID query (only if not already added)
    for (const doc of createdBySnapshot.docs) {
      if (!allCompanies.has(doc.id)) {
        allCompanies.set(doc.id, { id: doc.id, ...doc.data() } as Company)
      }
    }

    return Array.from(allCompanies.values())
  } catch (error) {
    throw new Error(`Failed to fetch owned companies: ${error}`)
  }
}

// Mutation Functions - Keep as is (will be refactored for useMutation later)

export const createCompany = async (companyInput: CompanyInput, ownerID: string) => {
  const companyID = doc(collection(db, COMPANIES_COLLECTION)).id
  const now = Timestamp.now()
  const newCompany: Company = {
    ...companyInput,
    id: companyID,
    createdByUserID: ownerID,
    createdAt: now,
    updatedAt: now,
    status: true,
    usersCount: 1,
    followRequestsCount: 0,
  }
  const docRef = doc(db, COMPANIES_COLLECTION, companyID)
  await setDoc(docRef, newCompany)
  await createCompanyUser({ companyID, userID: ownerID, role: CompanyUserRole.ADMIN })
  return newCompany as Company
}

export const updateCompany = async (companyID: string, companyInput: Partial<CompanyInput>) => {
  const docRef = doc(db, COMPANIES_COLLECTION, companyID)
  const docSnap = await getDoc(docRef)
  if (!docSnap.exists()) throw new Error('Company not found')
  const now = Timestamp.now()
  await updateDoc(docRef, { ...companyInput, updatedAt: now })
  return { ...docSnap.data(), ...companyInput, updatedAt: now } as Company
}

export const deleteCompany = async (companyID: string) => {
  const docRef = doc(db, COMPANIES_COLLECTION, companyID)
  await deleteDoc(docRef)
  return true
}

export const createCompanyUser = async (input: CompanyUserInput, skipCountEvent = false) => {
  try {
    const now = Timestamp.now()
    const id = newCompanyUserID(input.companyID, input.userID)
    const newCompanyUser: CompanyUser = { ...input, id, createdAt: now }
    const docRef = doc(db, COMPANIES_USERS_COLLECTION, id)
    await setDoc(docRef, newCompanyUser)

    // Create user count event (unless skipped for specific use cases)
    if (!skipCountEvent && input.companyID && input.userID && input.role) {
      try {
        await notificationService.createUserCountEvent({
          companyId: input.companyID!,
          action: 'increment',
          userId: input.userID!,
          reason: 'user_added',
          metadata: {
            role: input.role.toString(),
            companyUserId: id,
          },
        })
      } catch (notificationError) {
        console.error('Error creating user addition count event:', notificationError)
      }
    }

    return newCompanyUser
  } catch (error: any) {
    throw new Error(`Failed to create company user: ${error.message}`)
  }
}

export const updateCompanyStatus = async (companyID: string, status: boolean) => {
  try {
    const docRef = doc(db, COMPANIES_COLLECTION, companyID)
    const docSnap = await getDoc(docRef)
    if (!docSnap.exists()) throw new Error('Company not found')
    const now = Timestamp.now()
    await updateDoc(docRef, {
      status,
      updatedAt: now,
      ...(status === false ? { canCreatePosts: false } : {}),
    })
    return {
      ...docSnap.data(),
      status,
      updatedAt: now,
      ...(status === false ? { canCreatePosts: false } : {}),
    } as Company
  } catch (error: any) {
    throw new Error(`Failed to update company status: ${error.message}`)
  }
}

export const removeCompanyUser = async (companyID: string, userID: string, skipCountEvent = false): Promise<void> => {
  if (!companyID || companyID.trim() === '') {
    throw new Error('Company ID is required')
  }
  if (!userID || userID.trim() === '') {
    throw new Error('User ID is required')
  }

  try {
    const id = newCompanyUserID(companyID, userID)
    const docRef = doc(db, COMPANIES_USERS_COLLECTION, id)

    // Get user role before deletion for count event
    let userRole: CompanyUserRole | undefined
    if (!skipCountEvent) {
      try {
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          userRole = docSnap.data().role
        }
      } catch (error) {
        console.warn('Could not retrieve user role before deletion:', error)
      }
    }

    await deleteDoc(docRef)

    // Create user count event (unless skipped)
    if (!skipCountEvent && userRole) {
      try {
        await notificationService.createUserCountEvent({
          companyId: companyID,
          action: 'decrement',
          userId: userID,
          reason: 'user_removed',
          metadata: {
            role: userRole.toString(),
            companyUserId: id,
          },
        })
      } catch (notificationError) {
        console.error('Error creating user removal count event:', notificationError)
      }
    }
  } catch (error: any) {
    throw new Error(`Failed to remove company user: ${error.message}`)
  }
}

export const getAllCompanyUsers = async (): Promise<CompanyUser[]> => {
  try {
    const q = query(collection(db, COMPANIES_USERS_COLLECTION))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as CompanyUser)
  } catch (error) {
    throw new Error(`Failed to fetch all company users: ${(error as Error).message}`)
  }
}

export const getCompanyUser = async (companyID: string, userID: string): Promise<CompanyUser | null> => {
  if (!companyID || companyID.trim() === '') {
    throw new Error('Company ID is required')
  }
  if (!userID || userID.trim() === '') {
    throw new Error('User ID is required')
  }

  try {
    const id = newCompanyUserID(companyID, userID)
    const docRef = doc(db, COMPANIES_USERS_COLLECTION, id)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    return { id: docSnap.id, ...docSnap.data() } as CompanyUser
  } catch (error: any) {
    throw new Error(`Failed to fetch company user: ${error.message}`)
  }
}

export const validateEmailForOwnership = async (email: string): Promise<{ canBeOwner: boolean; reason?: string }> => {
  try {
    const normalizedEmail = email.toLowerCase().trim()

    // Check if user exists
    const usersQuery = query(collection(db, 'users'), where('email', '==', normalizedEmail))
    const usersSnapshot = await getDocs(usersQuery)

    if (usersSnapshot.empty) {
      // Email doesn't exist - can be used (will create account)
      return { canBeOwner: true }
    }

    const userDoc = usersSnapshot.docs[0]
    const userId = userDoc.id

    // Check if user already owns a company
    const ownedCompanies = await getOwnedCompanies(userId)
    if (ownedCompanies.length > 0) {
      return { canBeOwner: false, reason: 'User already owns a company' }
    }

    // Check if user is already a member of any company
    const joinedCompanies = await getUserJoinedCompanies(userId)
    if (joinedCompanies.length > 0) {
      return { canBeOwner: false, reason: 'User is already a member of a company' }
    }

    // User exists but doesn't own or belong to any company - can be owner
    return { canBeOwner: true }
  } catch (error) {
    throw new Error('Failed to validate email for ownership')
  }
}

export const createCompanyWithOwnerInvite = async (
  companyInput: CompanyInput,
  ownerEmail: string,
  superAdminId: string,
): Promise<Company> => {
  const normalizedEmail = ownerEmail.toLowerCase().trim()

  // Generate invitation token and expiration (7 days)
  const inviteToken = crypto.randomUUID()
  const expiresAt = Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))

  // Create company with pending invitation
  const companyData = {
    ...companyInput,
    createdByUserID: superAdminId,
    // currentOwnerID is undefined - no owner yet
    pendingOwnerEmail: normalizedEmail,
    inviteToken,
    inviteExpiresAt: expiresAt,
    status: true,
    usersCount: 0,
    followRequestsCount: 0,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  }

  const docRef = await addDoc(collection(db, 'companies'), companyData)
  const company = { id: docRef.id, ...companyData }

  // Send ownership invitation email
  try {
    // Get super admin user data for the email
    const superAdminDoc = await getDoc(doc(db, 'users', superAdminId))
    const superAdminName = superAdminDoc.exists() ? superAdminDoc.data().fullName || 'Super Admin' : 'Super Admin'

    // Call the Cloud Function to send the email
    const requestBody = {
      companyId: company.id,
      companyName: company.name,
      inviteToken,
      toEmail: normalizedEmail,
      superAdminName: superAdminName,
    }

    const cloudFunctionUrl = process.env.NEXT_PUBLIC_FIREBASE_FUNCTIONS_URL
    if (!cloudFunctionUrl) {
      throw new Error('Cloud Functions URL not configured')
    }

    const response = await fetch(`${cloudFunctionUrl}/sendCompanyOwnershipInvite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorText = await response.text()
    }
  } catch (emailError) {
    // This is expected when Cloud Functions are not configured/running
  }

  return company
}

export const validateCompanyInvitation = async (
  token: string,
): Promise<{
  companyId: string
  companyName: string
  inviteToken: string
  pendingOwnerEmail: string
  inviteExpiresAt: Date
  isValid: boolean
  isExpired: boolean
}> => {
  try {
    // Find company with this invitation token (simplified query to avoid index requirement)
    const companiesQuery = query(collection(db, 'companies'), where('inviteToken', '==', token), limit(1))

    const companiesSnapshot = await getDocs(companiesQuery)

    if (companiesSnapshot.empty) {
      return {
        companyId: '',
        companyName: '',
        inviteToken: token,
        pendingOwnerEmail: '',
        inviteExpiresAt: new Date(),
        isValid: false,
        isExpired: false,
      }
    }

    const companyDoc = companiesSnapshot.docs[0]
    const companyData = companyDoc.data() as Company

    // Additional validation: check if there's actually a pending invitation
    if (!companyData.pendingOwnerEmail || !companyData.inviteToken) {
      return {
        companyId: '',
        companyName: '',
        inviteToken: token,
        pendingOwnerEmail: '',
        inviteExpiresAt: new Date(),
        isValid: false,
        isExpired: false,
      }
    }

    const isExpired = companyData.inviteExpiresAt ? companyData.inviteExpiresAt.toDate() < new Date() : true

    return {
      companyId: companyDoc.id,
      companyName: companyData.name || '',
      inviteToken: token,
      pendingOwnerEmail: companyData.pendingOwnerEmail || '',
      inviteExpiresAt: companyData.inviteExpiresAt?.toDate() || new Date(),
      isValid: true,
      isExpired,
    }
  } catch (error) {
    throw new Error('Failed to validate invitation')
  }
}

export const acceptCompanyInvitation = async (token: string, userId: string): Promise<Company> => {
  // Validate invitation first
  const invitation = await validateCompanyInvitation(token)

  if (!invitation.isValid) {
    throw new Error('Invalid invitation token')
  }

  if (invitation.isExpired) {
    throw new Error('Invitation has expired')
  }

  // Get user data to verify email matches
  const userDoc = await getDoc(doc(db, 'users', userId))
  if (!userDoc.exists()) {
    throw new Error('User not found')
  }

  const userData = userDoc.data()
  if (userData.email?.toLowerCase() !== invitation.pendingOwnerEmail.toLowerCase()) {
    throw new Error('Email does not match invitation')
  }

  // Update company to set new owner and clear pending invitation
  const companyRef = doc(db, 'companies', invitation.companyId)
  await updateDoc(companyRef, {
    currentOwnerID: userId,
    pendingOwnerEmail: deleteField(),
    inviteToken: deleteField(),
    inviteExpiresAt: deleteField(),
    updatedAt: Timestamp.now(),
  })

  // Add user to companiesUsers as ADMIN
  await createCompanyUser({
    companyID: invitation.companyId,
    userID: userId,
    role: CompanyUserRole.ADMIN,
  })

  // Get updated company data
  const updatedCompanyDoc = await getDoc(companyRef)
  const updatedCompany = { id: updatedCompanyDoc.id, ...updatedCompanyDoc.data() } as Company

  return updatedCompany
}

export const acceptInvitationWithAccountCreation = async (
  token: string,
  fullName: string,
  password: string,
): Promise<{ company: Company; user: any }> => {
  // Validate invitation first
  const invitation = await validateCompanyInvitation(token)

  if (!invitation.isValid) {
    throw new Error('Invalid invitation token')
  }

  if (invitation.isExpired) {
    throw new Error('Invitation has expired')
  }

  // Create user account
  const userCredential = await createUserWithEmailAndPassword(auth, invitation.pendingOwnerEmail, password)
  const userId = userCredential.user.uid

  // Create user document
  const userData = {
    fullName,
    email: invitation.pendingOwnerEmail,
    emailVerified: true,
    createdViaInvite: true,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  }

  await setDoc(doc(db, 'users', userId), userData)

  // Accept the invitation
  const company = await acceptCompanyInvitation(token, userId)

  return { company, user: { id: userId, ...userData } }
}

export const resendCompanyInvitation = async (companyId: string, superAdminId: string): Promise<Company> => {
  // Get company data
  const companyRef = doc(db, 'companies', companyId)
  const companyDoc = await getDoc(companyRef)

  if (!companyDoc.exists()) {
    throw new Error('Company not found')
  }

  const companyData = companyDoc.data() as Company

  if (!companyData.pendingOwnerEmail) {
    throw new Error('No pending invitation found for this company')
  }

  // Generate new invitation token and expiration
  const newInviteToken = crypto.randomUUID()
  const newExpiresAt = Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))

  // Update company with new token and expiration
  await updateDoc(companyRef, {
    inviteToken: newInviteToken,
    inviteExpiresAt: newExpiresAt,
    updatedAt: Timestamp.now(),
  })

  // Send new invitation email
  try {
    // Get super admin user data for the email
    const superAdminDoc = await getDoc(doc(db, 'users', superAdminId))
    const superAdminName = superAdminDoc.exists() ? superAdminDoc.data().fullName || 'Super Admin' : 'Super Admin'

    // Call the Cloud Function to send the email
    const requestBody = {
      companyId,
      companyName: companyData.name,
      inviteToken: newInviteToken,
      toEmail: companyData.pendingOwnerEmail,
      superAdminName: superAdminName,
    }

    const cloudFunctionUrl = process.env.NEXT_PUBLIC_FIREBASE_FUNCTIONS_URL
    if (!cloudFunctionUrl) {
      throw new Error('Cloud Functions URL not configured')
    }

    const response = await fetch(`${cloudFunctionUrl}/sendCompanyOwnershipInvite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorText = await response.text()
    }
  } catch (emailError) {
    // This is expected when Cloud Functions are not configured/running
  }

  // Get updated company data
  const updatedCompanyDoc = await getDoc(companyRef)
  if (!updatedCompanyDoc.exists()) {
    throw new Error('Company not found after update')
  }

  return { id: updatedCompanyDoc.id, ...updatedCompanyDoc.data() } as Company
}

export const parseCompany = (data: any): Company => {
  return {
    id: data.id || '',
    name: data.name || '',
    email: data.email || '',
    phoneNumber: data.phoneNumber || '',
    logoUrl: data.logoUrl || '',
    logo: data.logo || '',
    status: data.status ?? true,
    usersCount: data.usersCount || 0,
    followRequestsCount: data.followRequestsCount || 0,
    followersCount: data.followersCount || 0,
    likesCount: data.likesCount || 0,
    postsCount: data.postsCount || 0,
    address: data.address || '',
    city: data.city || '',
    state: data.state || '',
    location: data.location || '',
    createdByUserID: data.createdByUserID || '',
    currentOwnerID: data.currentOwnerID || '',
    pendingOwnerEmail: data.pendingOwnerEmail,
    inviteToken: data.inviteToken,
    inviteExpiresAt: data.inviteExpiresAt,
    createdAt: data.createdAt || Timestamp.now(),
    updatedAt: data.updatedAt || Timestamp.now(),
    following: data.following ?? false,
    posts: data.posts || 0,
    likes: data.likes || 0,
    followers: data.followers || 0,
  }
}

/**
 * Sync company counts based on actual documents
 * This function can be used to fix count discrepancies
 */
export const syncCompanyCounts = async (
  companyId: string,
): Promise<{
  followRequestsCount: number
  followersCount: number
  usersCount: number
}> => {
  try {
    // Method 1: Use cloud function for accurate syncing
    await notificationService.createCountSyncEvent({
      companyId,
      reason: 'manual_sync',
    })

    // Method 2: Return current counts from company document
    const companyRef = doc(db, COMPANIES_COLLECTION, companyId)
    const companyDoc = await getDoc(companyRef)

    if (!companyDoc.exists()) {
      throw new Error('Company not found')
    }

    const data = companyDoc.data()
    return {
      followRequestsCount: data.followRequestsCount || 0,
      followersCount: data.followersCount || 0,
      usersCount: data.usersCount || 0,
    }
  } catch (error: any) {
    throw new Error(`Failed to sync company counts: ${error.message}`)
  }
}
